import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import {
  Observable,
  Subject,
  filter,
  firstValueFrom,
  merge,
  switchMap,
} from "rxjs";

import {
  Payment,
  PaymentStatusEnum,
  PaymentTypeEnum,
} from "@services/business/_models/interfaces/payment.interfaces";
import {
  LegalRecord,
  RecordStatus,
} from "@services/business/_models/interfaces/record.interfaces";
import { AccessRoleEnum } from "@services/business/_models/roles";

import { LegalRecordStateService } from "@core/services/utils/legal-record-state.service";
import { processHttpError } from "@core/services/utils/process-http-error";
import { ResultService } from "@core/services/utils/result.service";
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from "@ui/components/confirmation-dialog/confirmation-dialog.component";
import { BePaymentsService } from "../payments-services/be-payments.service";
import { PaymentsHelperService } from "../payments-services/payments-helper.service";
import { BeUsersService } from "../user-services/be-users.service";
import { BeLegalRecordsService } from "./be-legal-records.service";

export interface RecordStatusData {
  recordId: string;
  status: RecordStatus;
  role?: AccessRoleEnum;
  displayActions: { status: RecordStatus; label: string }[];
  paymentActions: Payment[];
  availableActions: RecordStatus[];
}

@Injectable({
  providedIn: "root",
})
export class RecordStatusService {
  private readonly legalRecordService = inject(BeLegalRecordsService);
  private readonly legalRecordStateService = inject(LegalRecordStateService);
  private readonly paymentService = inject(BePaymentsService);
  private readonly paymentsHelper = inject(PaymentsHelperService);
  private readonly translateService = inject(TranslateService);
  private readonly resultService = inject(ResultService);
  private readonly dialog = inject(MatDialog);
  private readonly beUsersService = inject(BeUsersService);

  private readonly statusChanged$ = new Subject<string>();

  getRecordStatusData$(
    recordId: string
  ): Observable<RecordStatusData | undefined> {
    if (!recordId) {
      return new Observable(subscriber => {
        subscriber.next(undefined);
        subscriber.complete();
      });
    }

    const initialLoad$ = this.loadRecordStatusDataObservable(recordId);
    const statusChanges$ = this.statusChanged$.pipe(
      filter(id => id === recordId),
      switchMap(() => this.loadRecordStatusDataObservable(recordId))
    );

    return merge(initialLoad$, statusChanges$);
  }

  private loadRecordStatusDataObservable(
    recordId: string
  ): Observable<RecordStatusData | undefined> {
    return new Observable(subscriber => {
      this.loadRecordStatusData(recordId)
        .then(data => {
          subscriber.next(data);
          subscriber.complete();
        })
        .catch(error => {
          processHttpError(error);
          subscriber.next(undefined);
          subscriber.complete();
        });
    });
  }

  async loadRecordStatusData(
    recordId: string
  ): Promise<RecordStatusData | undefined> {
    if (!recordId) {
      return;
    }

    const record = await firstValueFrom(
      this.legalRecordStateService.getRecordInfo(recordId)
    );

    if (!record) {
      return;
    }

    const availableActions = await this.loadAvailableActionsRaw(recordId);
    const userRoles = await this.beUsersService.loadRoles();
    const primaryRole = this.beUsersService.determinePrimaryRole(userRoles);

    return {
      recordId,
      status: record.status ?? RecordStatus.ENGAGED,
      role: primaryRole,
      displayActions: this.processActions(availableActions),
      paymentActions: await this.loadPaymentActions(recordId),
      availableActions: availableActions,
    };
  }

  async loadAvailableActions(
    recordId: string
  ): Promise<{ status: RecordStatus; label: string }[]> {
    if (!recordId) {
      return [];
    }

    try {
      const actions =
        await this.legalRecordService.getAvailableActions(recordId);
      return this.processActions(actions);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        processHttpError(error);
      }

      return [];
    }
  }

  private async loadAvailableActionsRaw(
    recordId: string
  ): Promise<RecordStatus[]> {
    try {
      const actions =
        await this.legalRecordService.getAvailableActions(recordId);
      return actions;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        processHttpError(error);
      }

      return [];
    }
  }

  async loadPaymentActions(recordId: string): Promise<Payment[]> {
    if (!recordId) {
      return [];
    }

    const data = await this.paymentService.searchPayments(
      { recordIds: [recordId] },
      1,
      10,
      false
    );

    if (data.data && data.data.length > 0) {
      return this.processPayments(data.data);
    }
    return [];
  }

  async handleStatusChange(
    recordId: string,
    newStatus: RecordStatus
  ): Promise<boolean> {
    try {
      const dialogConfig = this.getDialogConfig(newStatus);
      if (!dialogConfig) {
        return false;
      }

      const confirmed = await firstValueFrom(
        this.showConfirmationDialog(dialogConfig)
      );

      if (!confirmed) {
        return false;
      }

      const legalRecord: LegalRecord = { status: newStatus };
      const response = await this.legalRecordService.setRecordStatus(
        recordId,
        legalRecord
      );

      if (response?.ok) {
        this.resultService.openSuccess(
          this.translateService.instant("ResultActions.ActionSuccessful")
        );
        this.refreshRecordStatus(recordId);
        return true;
      }
      return false;
    } catch (error) {
      processHttpError(error);
      this.resultService.openError(
        this.translateService.instant("ResultActions.ActionFailed")
      );
      return false;
    }
  }

  handlePaymentAction(payment: Payment): void {
    this.paymentsHelper.handlePayment(payment);
  }

  private refreshRecordStatus(recordId: string): void {
    this.statusChanged$.next(recordId);
  }

  private processActions(
    actions: RecordStatus[]
  ): { status: RecordStatus; label: string }[] {
    return actions
      .filter(action => !action.startsWith("info_"))
      .map(status => ({
        status,
        label: this.getActionLabel(status),
      }));
  }

  private processPayments(payments: Payment[]): Payment[] {
    return payments
      .filter(payment => payment.status === PaymentStatusEnum.requested)
      .filter(
        payment =>
          payment.type === PaymentTypeEnum.credit_card ||
          payment.type === PaymentTypeEnum.sepa
      );
  }

  private getActionLabel(status: RecordStatus): string {
    const labelMap: Partial<Record<RecordStatus, string>> = {
      [RecordStatus.ENGAGED]: this.translateService.instant(
        "LegalRecordsComponent.EngageRecord"
      ),
      [RecordStatus.WAITING_FOR_PAYMENT]: this.translateService.instant(
        "Payments.AwaitingPayment"
      ),
      [RecordStatus.WAITING_FOR_SECOND_PAYMENT]: this.translateService.instant(
        "Payments.AwaitingPayment"
      ),
      [RecordStatus.WAITING_FOR_PAYMENT_FIX]: this.translateService.instant(
        "Payments.FixPaymentRequired"
      ),
      [RecordStatus.PAYMENT_FIXED]: this.translateService.instant(
        "Payments.PaymentFixed"
      ),
      [RecordStatus.CREDIT_DOWNPAYMENT]: this.translateService.instant(
        "Payments.CreditDownpayment"
      ),
      [RecordStatus.WORK_AUTHORIZED]:
        this.translateService.instant("Work.AuthorizeWork"),
      [RecordStatus.DEFINE_WORK]: this.translateService.instant(
        "Work.DefineWorkScope"
      ),
      [RecordStatus.WORK_DEFINED]: this.translateService.instant(
        "Work.MarkWorkAsDefined"
      ),
      [RecordStatus.PENDING_WORK_COMPLETION_CHECK]:
        this.translateService.instant("Work.CheckWorkCompletion"),
      [RecordStatus.WORK_PENDING]: this.translateService.instant(
        "Work.IndicateWorkPending"
      ),
      [RecordStatus.WORK_COMPLETED]: this.translateService.instant(
        "Work.MarkWorkAsCompleted"
      ),
      [RecordStatus.DELIVERY_AUTHORIZED]: this.translateService.instant(
        "Work.AuthorizeDelivery"
      ),
      [RecordStatus.RESULTS_DELIVERED]: this.translateService.instant(
        "Work.MarkResultsAsDelivered"
      ),
      [RecordStatus.CLOSED]: this.translateService.instant(
        "LegalRecordsComponent.CloseRecord"
      ),
      [RecordStatus.MIGRATION_REOPEN]: this.translateService.instant(
        "LegalRecordsComponent.ReopenRecord"
      ),
      [RecordStatus.ROLLBACK_TO_WORK_COMPLETION_CHECK]:
        this.translateService.instant(
          "LegalRecordsComponent.RollbackToWorkCompletion"
        ),
    };

    return labelMap[status] || status;
  }

  private getDialogConfig(
    status: RecordStatus
  ): ConfirmationDialogData | undefined {
    const statusConfigMap = new Map<
      RecordStatus,
      { messageKey: string; confirmKey: string }
    >([
      [
        RecordStatus.ENGAGED,
        {
          messageKey: "LegalRecordsComponent.ConfirmEngageRecord",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.WAITING_FOR_PAYMENT,
        {
          messageKey: "Payments.ConfirmCreditDownpayment",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.WAITING_FOR_PAYMENT_FIX,
        {
          messageKey: "Payments.ConfirmCreditDownpayment",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.PAYMENT_FIXED,
        {
          messageKey: "Payments.ConfirmPaymentFixed",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.CREDIT_DOWNPAYMENT,
        {
          messageKey: "Payments.ConfirmCreditDownpayment",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.WORK_AUTHORIZED,
        {
          messageKey: "Work.ConfirmAuthorizeWork",
          confirmKey: "App.Authorize",
        },
      ],
      [
        RecordStatus.DEFINE_WORK,
        { messageKey: "Work.ConfirmDefineWork", confirmKey: "App.Proceed" },
      ],
      [
        RecordStatus.WORK_DEFINED,
        { messageKey: "Work.ConfirmWorkDefined", confirmKey: "App.Confirm" },
      ],
      [
        RecordStatus.PENDING_WORK_COMPLETION_CHECK,
        {
          messageKey: "Work.ConfirmPendingWorkCompletionCheck",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.WORK_PENDING,
        { messageKey: "Work.ConfirmWorkPending", confirmKey: "App.Confirm" },
      ],
      [
        RecordStatus.WORK_COMPLETED,
        { messageKey: "Work.ConfirmWorkCompleted", confirmKey: "App.Confirm" },
      ],
      [
        RecordStatus.DELIVERY_AUTHORIZED,
        {
          messageKey: "Work.ConfirmAuthorizeDelivery",
          confirmKey: "Work.Authorize",
        },
      ],
      [
        RecordStatus.RESULTS_DELIVERED,
        {
          messageKey: "Work.ConfirmResultsDelivered",
          confirmKey: "App.Confirm",
        },
      ],
      [
        RecordStatus.CLOSED,
        {
          messageKey: "LegalRecordsComponent.ConfirmCloseRecord",
          confirmKey: "LegalRecordsComponent.CloseRecord",
        },
      ],
      [
        RecordStatus.MIGRATION_REOPEN,
        {
          messageKey: "LegalRecordsComponent.ConfirmReopenRecord",
          confirmKey: "LegalRecordsComponent.ReopenRecord",
        },
      ],
      [
        RecordStatus.ROLLBACK_TO_WORK_COMPLETION_CHECK,
        {
          messageKey: "LegalRecordsComponent.ConfirmRollbackToWorkCompletion",
          confirmKey: "App.Confirm",
        },
      ],
    ]);

    const config = statusConfigMap.get(status);
    if (!config) {
      return undefined;
    }

    return {
      message: this.translateService.instant(config.messageKey),
      confirmButtonText: this.translateService.instant(config.confirmKey),
      cancelButtonText: this.translateService.instant("App.Cancel"),
    };
  }

  private showConfirmationDialog(
    config: ConfirmationDialogData
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: config,
    });

    return dialogRef.afterClosed();
  }
}
